import { useState } from 'react';
import type { ReactNode } from 'react';
import { DATE_FORMATS, formatDateTime } from '@belong/common';
import { IconCaretDown, IconCaretUp, IconCheck } from '@belong/icons';
import { ButtonBase, Image, Text } from '@belong/ui';
import type {
  ImprovementsProgressItemModel,
  SpaceListingModel,
} from 'api/models';
import Label from 'components/Label/Label';
import { formatString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import {
  getDisplayableMedia,
  getRoomLabels,
  getSourceAdjective,
  getSourceLabel,
  getStatusBadge,
  getTradeLabel,
} from '../../utils';
import { ProgressItemMedia } from '../progress-item-media/progress-item-media';
import { RepairPlaceholder } from '../repair-placeholder/repair-placeholder';

type Props = {
  item: ImprovementsProgressItemModel;
  spaces: SpaceListingModel[];
  previousReportSubType?: string;
};

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-xs">
      <IconCheck width={16} className="mt-[2px] shrink-0 text-green" />
      <Text variant="p1">
        {`${label} : `}
        <Text
          as="span"
          variant="p1"
          fontWeight="semibold"
          className="text-navy"
        >
          {value}
        </Text>
      </Text>
    </div>
  );
}

export function ProgressItemCard({
  item,
  spaces,
  previousReportSubType,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const roomLabels = getRoomLabels(item.owners, spaces);
  const badge = getStatusBadge(item.status);
  const thumbnailUrl = getDisplayableMedia(item.media)[0]?.url;
  const sourceAdjective = getSourceAdjective(previousReportSubType);
  const sourceLabel = getSourceLabel(previousReportSubType);
  // `summary` is optional on the model, so joining rather than interpolating
  // keeps an absent one from leaving a dangling ' · ' before the date.
  const appointmentDetail = item.appointmentDate
    ? [
        item.summary,
        formatDateTime({
          dateTime: item.appointmentDate,
          format: DATE_FORMATS.MONTH_DAY_SHORT,
        }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  // An unmapped/absent source drops out entirely rather than leaving a
  // leading separator or space in front of the trade.
  const summaryLine = [
    sourceAdjective &&
      formatString(POST_INSPECTION_FLOW_STRINGS['progress.item.summary'], {
        sourceAdjective,
      }),
    getTradeLabel(item.trade),
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <div className="rounded border border-solid border-gray p-sm">
      <ButtonBase
        className="flex w-full items-center gap-sm text-left"
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <div className="h-[60px] w-[60px] shrink-0 overflow-hidden rounded-small border border-solid border-gray">
          {thumbnailUrl ? (
            <Image alt="" src={thumbnailUrl} className="object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <RepairPlaceholder size={40} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <Text fontWeight="semibold" className="text-navy">
            {item.summary}
          </Text>
          <Text variant="p1" className="text-dark-gray">
            {summaryLine}
          </Text>
          {roomLabels.length > 0 && (
            <Text variant="p1" className="text-gray">
              {roomLabels.join(', ')}
            </Text>
          )}
        </div>

        {badge && (
          <Label className="shrink-0" color={badge.color} text={badge.label} />
        )}

        <div className="shrink-0 text-navy">
          {isExpanded ? (
            <IconCaretUp width={14} height={14} />
          ) : (
            <IconCaretDown width={14} height={14} />
          )}
        </div>
      </ButtonBase>

      {isExpanded && (
        <div className="mt-sm border-0 border-t border-dashed border-gray pt-sm">
          {item.description && (
            <Text className="mb-sm">{item.description}</Text>
          )}

          <div className="flex flex-col gap-xs">
            {item.completedOn && (
              <DetailRow
                label={POST_INSPECTION_FLOW_STRINGS['progress.item.completed']}
                value={formatDateTime({
                  dateTime: item.completedOn,
                  format: DATE_FORMATS.FULL_MONTH_DAY_YEAR,
                })}
              />
            )}

            {appointmentDetail && (
              <DetailRow
                label={
                  POST_INSPECTION_FLOW_STRINGS['progress.item.appointment']
                }
                value={appointmentDetail}
              />
            )}

            {sourceLabel && (
              <DetailRow
                label={POST_INSPECTION_FLOW_STRINGS['progress.item.source']}
                value={sourceLabel}
              />
            )}
          </div>

          <ProgressItemMedia media={item.media} />
        </div>
      )}
    </div>
  );
}
